import { formatTimeDifference } from '@/lib/utils'

import { type Task, TaskStatus } from '@droidmap/project-service-contract'

/**
 * How long a job ran, and how big the thing it ran on was.
 *
 * Runtime is deliberately never presented on its own. Two jobs on *identical*
 * input and instance type were measured at 1635 s and 10927 s purely from a
 * `matcherNeighbors` change (droidmap#2476) — so a bare duration column looks
 * erratic and invites the wrong conclusion. Pairing every duration with the
 * input it processed is what makes one run readable against another.
 */
export interface TaskRuntime {
  /**
   * Wall-clock milliseconds from first start. `undefined` when the task never
   * started — still queued, or it failed before it got compute. Distinct from
   * `0`, which would claim an instant run.
   */
  durationMs?: number
  /** The clock is still moving: the value re-derives on every tick. */
  live: boolean
  /** Gigapixels of input imagery, when the task recorded a size. */
  gigapixels?: number
  /** Input image count, when the task recorded one. */
  imageCount?: number
}

/**
 * Dates cross the wire as ISO strings even though the contract types them as
 * `Date`, so every read has to normalise. Returns `undefined` for absent or
 * unparseable values rather than an `Invalid Date` that poisons arithmetic.
 */
const toTime = (value: Date | string | undefined): number | undefined => {
  if (!value) return undefined
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime()
  return Number.isNaN(ms) ? undefined : ms
}

/** Statuses at which the runtime clock has stopped for good. */
const TERMINAL_STATUSES: readonly TaskStatus[] = [
  TaskStatus.COMPLETED,
  TaskStatus.FAILED,
  TaskStatus.CANCELLED,
]

export const isTerminalStatus = (status: TaskStatus): boolean =>
  TERMINAL_STATUSES.includes(status)

/**
 * Derive a task's runtime and input size. `now` is injectable so a ticking
 * caller can share one clock across a whole table (and so tests are
 * deterministic).
 *
 * A running task measures `now - startedAt`; a finished one measures
 * `endedAt - startedAt`. A finished task that somehow has no `endedAt`
 * (an older row, or an event that never landed) reports no duration rather
 * than a runtime that keeps growing forever after the job stopped.
 */
export const getTaskRuntime = (
  task: Pick<
    Task,
    'status' | 'startedAt' | 'endedAt' | 'inputGigapixels' | 'inputImageCount'
  >,
  now: number = Date.now(),
): TaskRuntime => {
  const startedAt = toTime(task.startedAt)
  const endedAt = toTime(task.endedAt)
  const terminal = isTerminalStatus(task.status)
  const live = !terminal && endedAt === undefined && startedAt !== undefined

  let durationMs: number | undefined
  if (startedAt !== undefined) {
    if (endedAt !== undefined) {
      durationMs = Math.max(0, endedAt - startedAt)
    } else if (!terminal) {
      durationMs = Math.max(0, now - startedAt)
    }
  }

  return {
    ...(durationMs !== undefined && { durationMs }),
    live,
    ...(task.inputGigapixels !== undefined && {
      gigapixels: task.inputGigapixels,
    }),
    ...(task.inputImageCount !== undefined && {
      imageCount: task.inputImageCount,
    }),
  }
}

/** `1h 4m 12s`, reusing the same formatter the per-stage durations render with. */
export const formatDuration = (durationMs: number): string =>
  formatTimeDifference(0, durationMs)

/**
 * `8.64 GP`. Two significant-ish decimals below 10 GP, one above — small jobs
 * need the resolution to be distinguishable, large ones don't.
 */
export const formatGigapixels = (gigapixels: number): string =>
  `${gigapixels < 10 ? gigapixels.toFixed(2) : gigapixels.toFixed(1)} GP`

/**
 * The size line that sits under a duration — `8.64 GP · 412 img`, or just the
 * pixel total when no image count was recorded. `undefined` when the task has
 * no recorded size at all, so callers render nothing rather than an empty
 * separator.
 */
export const formatInputSize = (runtime: TaskRuntime): string | undefined => {
  if (runtime.gigapixels === undefined) return undefined
  const size = formatGigapixels(runtime.gigapixels)
  return runtime.imageCount === undefined
    ? size
    : `${size} · ${runtime.imageCount.toLocaleString()} img`
}

/**
 * Runtime normalised by input size, in ms per gigapixel — the axis on which
 * two differently-sized runs of the same project become comparable.
 *
 * `undefined` when either half is missing or the input sized to zero. Note
 * this rate is *not* stable across settings: the matcher regime (#2476) moves
 * it by ~6.7×, which is precisely why the UI reports it next to the settings
 * context rather than as a headline efficiency number.
 */
export const getMsPerGigapixel = (runtime: TaskRuntime): number | undefined => {
  if (runtime.durationMs === undefined) return undefined
  if (!runtime.gigapixels) return undefined
  return runtime.durationMs / runtime.gigapixels
}

/** `4m 12s / GP`, or `undefined` when the rate can't be derived. */
export const formatThroughput = (runtime: TaskRuntime): string | undefined => {
  const rate = getMsPerGigapixel(runtime)
  if (rate === undefined) return undefined
  return `${formatDuration(rate)} / GP`
}

/**
 * A full one-line description for a tooltip: what it ran for, over what, at
 * what rate. Undefined when there's nothing worth saying.
 */
export const describeRuntime = (runtime: TaskRuntime): string | undefined => {
  if (runtime.durationMs === undefined) return formatInputSize(runtime)
  const parts = [
    `${formatDuration(runtime.durationMs)}${runtime.live ? ' elapsed' : ''}`,
  ]
  const size = formatInputSize(runtime)
  if (size) parts.push(`over ${size}`)
  const throughput = formatThroughput(runtime)
  if (throughput) parts.push(`≈ ${throughput}`)
  return parts.join(' · ')
}

/**
 * How wide a size window still counts as "the same kind of run".
 *
 * Runtime scales with input size, so a 2 GP job and a 20 GP job in the same
 * project tell you nothing about each other. ±25% is tight enough that a
 * comparison is meaningful and loose enough to still find neighbours in a
 * project where every flight differs a little.
 */
export const COMPARABLE_SIZE_TOLERANCE = 0.25

/**
 * Whether `candidate` is close enough in input size to `reference` for their
 * runtimes to be worth comparing directly. Unsized runs are never comparable —
 * "we don't know how big it was" is not the same as "it was the same size".
 */
export const isComparableSize = (
  reference: TaskRuntime,
  candidate: TaskRuntime,
): boolean => {
  if (!reference.gigapixels || !candidate.gigapixels) return false
  const ratio = candidate.gigapixels / reference.gigapixels
  return (
    ratio >= 1 - COMPARABLE_SIZE_TOLERANCE &&
    ratio <= 1 + COMPARABLE_SIZE_TOLERANCE
  )
}

/** A finished prior run, ready for the comparison list. */
export interface PreviousRun {
  task: Task
  runtime: TaskRuntime
  /** Input size is within {@link COMPARABLE_SIZE_TOLERANCE} of the subject. */
  comparable: boolean
}

/**
 * Pick the prior runs worth showing beside `subject`: same project, finished,
 * with a measured runtime, most recent first.
 *
 * Only *finished* runs qualify — another job still in flight has no final
 * number to compare against, and showing its live elapsed time next to a
 * completed duration reads as "this one was faster" when it simply hasn't
 * got there yet.
 */
export const selectPreviousRuns = (
  subject: Task,
  candidates: Task[],
  limit = 5,
): PreviousRun[] => {
  const subjectRuntime = getTaskRuntime(subject)
  return candidates
    .filter(
      (task) =>
        task.id !== subject.id &&
        task.type === subject.type &&
        isTerminalStatus(task.status),
    )
    .map((task) => ({ task, runtime: getTaskRuntime(task) }))
    .filter(({ runtime }) => runtime.durationMs !== undefined)
    .sort(
      (a, b) =>
        (toTime(b.task.startedAt) ?? toTime(b.task.created) ?? 0) -
        (toTime(a.task.startedAt) ?? toTime(a.task.created) ?? 0),
    )
    .slice(0, limit)
    .map(({ task, runtime }) => ({
      task,
      runtime,
      comparable: isComparableSize(subjectRuntime, runtime),
    }))
}

/**
 * Median runtime of the size-comparable runs among `runs` — the baseline a
 * job in flight is actually worth being read against.
 *
 * Median, not mean: a single interrupted or matcher-saturated outlier would
 * drag a mean far enough to make the baseline useless, and small projects
 * have too few runs to trim. `undefined` when nothing comparable exists,
 * which the UI must say out loud rather than quietly widening the window.
 */
export const medianComparableRuntimeMs = (
  runs: PreviousRun[],
): number | undefined => {
  const durations = runs
    .filter((run) => run.comparable)
    .map((run) => run.runtime.durationMs)
    .filter((ms): ms is number => ms !== undefined)
    .sort((a, b) => a - b)
  if (durations.length === 0) return undefined
  const mid = Math.floor(durations.length / 2)
  // Both reads are in range — the empty case returned above — so the
  // fallbacks only exist to keep the arithmetic total.
  const upper = durations[mid] ?? 0
  if (durations.length % 2 === 1) return upper
  return ((durations[mid - 1] ?? upper) + upper) / 2
}
